import { Component, Input, EventEmitter, Output, SimpleChanges, OnChanges } from '@angular/core';
import { FormGroup, FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { catchError, Observable, Subscription, tap } from 'rxjs';

import { labelMaxLength, labelMinLength } from '../../app.constants';
import { ConfigService } from '../../config.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { QueriesDialogComponent } from '../../queries/queries-dialog/queries-dialog.component';
import { SessionService } from '../session.service';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { Subject } from '../../shared/models/subject.model';
import { ChangeManagerService } from '../../change-manager.service';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FwIconModule, FwTooltipModule } from '@flywheel-io/vision';
import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';
import { ProjectsService } from 'app/src/projects/projects.service';
import { CoreApiService } from 'app/src/core-api.service';
import { DialogService } from '../../dialogs';

interface SelectOption {
  value: string,
  label?: string,
}

@Component({
  host: { 'test-id': 'subject-info' },
  selector: 'subject-info',
  templateUrl: './subject-info.component.html',
  styleUrls: ['./subject-info.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    DragDropModule,
    FileModule,
    FormsModule,
    FwIconModule,
    FwTooltipModule,
    ReactiveFormsModule,
    MaterialModule,
    PipesModule,
    SharedModule,
    RouterModule,
  ],
  providers: [
    ProjectsService,
    CoreApiService,
  ],
})
export class SubjectInfoComponent implements OnChanges {
  @Input() subject: Subject
  @Input('permissions') permissionSet = new Set<PermissionAction>()

  @Output() afterSave = new EventEmitter<Subject>()

  metadata = new FormGroup({
    label: new FormControl('', [Validators.required, Validators.minLength(labelMinLength), Validators.maxLength(labelMaxLength)]),
    type: new FormControl(''),
    firstname: new FormControl(''),
    lastname: new FormControl(''),
    dateOfBirth: new FormControl(null as Date | string),
    state: new FormControl(''),
    sex: new FormControl(''),
    cohort: new FormControl(''),
    mlset: new FormControl(''),
    race: new FormControl(''),
    ethnicity: new FormControl(''),
    species: new FormControl(''),
    strain: new FormControl(''),
  })

  options: { [key: string]: SelectOption[] } = {}

  permissions = {
    editMetadata: false,
    deleteContainer: false,
    viewAuditTrail: false,
  }

  private subscriptions = {
    versionCheck: Subscription.EMPTY,
  }

  constructor(
    private changeManager: ChangeManagerService,
    private config: ConfigService,
    private dialog: DialogService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private sessionService: SessionService,
  ) {
    this.options.type = [
      { value: 'human' },
      { value: 'animal' },
      { value: 'phantom' },
    ];
    this.options.state = [
      { value: 'In utero' },
      { value: 'Alive' },
      { value: 'Dead' },
    ],
      this.options.sex = [
        { value: 'female' },
        { value: 'male' },
        { value: 'other' },
        { value: 'unknown', label: 'Unknown or Not Reported' },
      ];
    this.options.cohort = [
      { value: 'Control' },
      { value: 'Study' },
    ];
    this.options.mlset = [
      { value: 'Training' },
      { value: 'Testing' },
      { value: 'Validation' },
    ];
    this.options.race = [
      { value: 'American Indian or Alaska Native' },
      { value: 'Asian' },
      { value: 'Native Hawaiian or Other Pacific Islander' },
      { value: 'Black or African American' },
      { value: 'White' },
      { value: 'More Than One Race' },
      { value: 'Unknown or Not Reported' },
    ];
    this.options.ethnicity = [
      { value: 'Hispanic or Latino' },
      { value: 'Not Hispanic or Latino' },
      { value: 'Unknown or Not Reported' },
    ];
    this.options.species = [
      { value: 'mouse' },
      { value: 'rat' },
      { value: 'other' },
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.permissionSet) {
      this.permissions = {
        editMetadata: this.permissionSet.has(PermissionAction.ContainersModifyMetadata),
        deleteContainer: this.permissionSet.has(PermissionAction.ContainersDeleteHierarchy),
        viewAuditTrail: this.permissionSet.has(PermissionAction.ContainersViewMetadata) && this.config.isFeatureEnabled('audit_trail'),
      };
      if (this.permissions.editMetadata) {
        this.changeManager.registerForm('SubjectInfoComponent', {
          control: this.metadata,
          reset: () => this.resetForm(),
          save: () => this._saveChanges(),
          successMessageKey: 'session.saveSubject.success',
          errorMessageKey: 'session.saveSubject.error',
        });
      }
    }
    if (changes.subject && this.subject?._id
      && (this.metadata.pristine || changes.subject.firstChange || this.subject?._id !== changes.subject.previousValue?._id)) {
      this.resetForm();
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
    this.changeManager.deregisterForm('SubjectInfoComponent');
  }

  get type(): string {
    return this.metadata.get('type').value;
  }

  resetForm(): void {
    // add options for unknown values
    for (const [field, options] of Object.entries(this.options)) {
      if (this.subject[field] && !options.some(option => option.value === this.subject[field])) {
        options.push({ value: this.subject[field] });
      }
    }
    this.metadata.reset({
      label: this.subject.label || '',
      type: this.subject.type || '',
      firstname: this.subject.firstname || '',
      lastname: this.subject.lastname || '',
      dateOfBirth: this.subject.date_of_birth || '',
      state: this.subject.state || '',
      sex: this.subject.sex || '',
      cohort: this.subject.cohort || '',
      mlset: this.subject.mlset || '', // version >= 15.6.0
      race: this.subject.race || '',
      ethnicity: this.subject.ethnicity || '',
      species: this.subject.species || '',
      strain: this.subject.strain || '',
    });
    this.metadata.markAsPristine();
    this.metadata.updateValueAndValidity();
  }

  private _saveChanges(): Observable<void> {
    const form = this.metadata.value;
    const payload: Partial<Subject> = {
      label: form.label,
      type: form.type || null,
    };
    // clear out any previously-set fields
    for (const field of ['firstname', 'lastname', 'species', 'strain']) {
      // some fields must be strings
      if (this.subject[field]) {
        payload[field] = '';
      }
    }
    for (const field of ['date_of_birth', 'state', 'sex', 'cohort', 'race', 'ethnicity']) {
      // some fields must be null
      if (this.subject[field]) {
        payload[field] = null;
      }
    }
    if (this.type === 'human' || !this.type) {
      Object.assign(payload, {
        firstname: form.firstname || '',
        lastname: form.lastname || '',
        date_of_birth: form.dateOfBirth || null,
        state: form.state || null,
        sex: form.sex || null,
        cohort: form.cohort || null,
        race: form.race || null,
        ethnicity: form.ethnicity || null,
      });
    }
    if (this.type === 'animal' || !this.type) {
      Object.assign(payload, {
        date_of_birth: form.dateOfBirth || null,
        state: form.state || null,
        sex: form.sex || null,
        cohort: form.cohort || null,
        species: form.species || '',
        strain: form.strain || '',
      });
    }
    if ((['human', 'animal'].includes(this.type) || !this.type)) {
      Object.assign(payload, { mlset: form.mlset || null });
    }
    return this.sessionService.saveSubject(this.subject._id, payload).pipe(
      tap(() => {
        this.metadata.markAsPristine();
        this.afterSave.emit({ ...this.subject, ...payload });
      }),
      catchError(error => {
        if (error.status === 422) {
          this.logger.warn(error);
          this.notifications.error('session.saveSubject.errorDuplicateCode');
        } else {
          this.logger.error(error);
        }
        throw error;
      }),
    );
  }

  saveChanges(): void {
    this._saveChanges().subscribe({
      next: () => {
        this.notifications.success('session.saveSubject.success');
      },
      error: error => {
        if (error.status !== 422) {
          this.notifications.error('session.saveSubject.error');
        }
      },
    });
  }

  deleteSubject(): void {
    this.sessionService.deleteSubject(this.subject).subscribe();
  }

  viewAuditTrail(): void {
    this.dialog.open(QueriesDialogComponent, {
      hasBackdrop: true,
      disableClose: false,
      height: '100%',
      width: '850px',
      panelClass: ['border-radius-0', 'padding-0'],
      position: { right: '0' },
      data: {
        auditable: this.subject,
        type: 'object',
      },
    });
  }
}
